// src/pages/en/EXIFPage_tw.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { parseNovelAIMetadata } from "../EXIF/novelaiMeta";
import "../EXIF/EXIFPage.css";

function EXIFPage() {
  const [darkMode, setDarkMode] = useState(false);

  // 行動裝置判定
  const [isMobile, setIsMobile] = useState(false);

  // 左側面板狀態
  const [files, setFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewHeight, setPreviewHeight] = useState(400);

  // 分頁狀態: 初始值 "NovelAI"
  const [activeTab, setActiveTab] = useState("NovelAI");

  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  const EMPTY_TEXT = "找不到資訊。";

  // NAI: 各欄位的輸出狀態
  const [naiPositiveDump, setNaiPositiveDump] = useState(EMPTY_TEXT);
  const [naiCharPositiveDump, setNaiCharPositiveDump] = useState(EMPTY_TEXT);
  const [naiNegativeDump, setNaiNegativeDump] = useState(EMPTY_TEXT);
  const [naiCharNegativeDump, setNaiCharNegativeDump] = useState(EMPTY_TEXT);
  const [naiModelDump, setNaiModelDump] = useState(EMPTY_TEXT);
  const [naiStealthDump, setNaiStealthDump] = useState(EMPTY_TEXT);

  // 目前頁面的 canonical URL (CSR 基準)
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI 圖片 EXIF 擷取工具",
      description:
        "TooLoves 的 AI 圖片 EXIF 擷取工具，可免費擷取 PNG、WebP 圖片中的 EXIF 與文字中繼資料。",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "PNG, WebP, 免費, EXIF 擷取, 中繼資料, NovelAI, Stealth PNG, 提示詞擷取, TooLoves",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "TWD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // 瀏覽器/行動裝置導覽列顏色
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const el = document.createElement("meta");
      el.name = "theme-color";
      el.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(el);
    }
  }, [darkMode]);

  // 行動裝置環境偵測: UA + matchMedia(pointer, width)
  useEffect(() => {
    const getIsMobile = () => {
      if (typeof window === "undefined") return false;
      const ua = navigator.userAgent || navigator.vendor || "";
      const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      const supportsMatch = typeof window.matchMedia === "function";
      const coarse = supportsMatch ? window.matchMedia("(pointer: coarse)").matches : false;
      const maxWidth = supportsMatch ? window.matchMedia("(max-width: 820px)").matches : false;

      return uaMobile || (coarse && maxWidth);
    };

    const apply = () => setIsMobile(getIsMobile());
    apply();

    const onChange = () => apply();

    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    const mqlCoarse = typeof window.matchMedia === "function" ? window.matchMedia("(pointer: coarse)") : null;
    const mqlMax = typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 820px)") : null;

    mqlCoarse && mqlCoarse.addEventListener && mqlCoarse.addEventListener("change", onChange);
    mqlMax && mqlMax.addEventListener && mqlMax.addEventListener("change", onChange);

    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
      mqlCoarse && mqlCoarse.removeEventListener && mqlCoarse.removeEventListener("change", onChange);
      mqlMax && mqlMax.removeEventListener && mqlMax.removeEventListener("change", onChange);
    };
  }, []);

  // 檔案上傳處理 (僅 UI)
  const addFiles = useCallback((list) => {
    const arr = Array.from(list || []);
    const imgs = arr.filter((f) => f.type.startsWith("image/"));
    const withId = imgs.map((f) => ({
      id: `${Date.now()}_${Math.random()}`,
      file: f,
      url: URL.createObjectURL(f),
    }));
    setFiles((prev) => {
      const merged = [...prev, ...withId];
      return merged;
    });
    if (withId.length > 0) setSelectedId(withId[withId.length - 1].id);
  }, []);

  const onPickClick = useCallback(() => fileInputRef.current?.click(), []);
  const onFileChange = useCallback(
    (e) => {
      if (e.target.files?.length) addFiles(e.target.files);
      // 允許同一檔案重複上傳
      e.target.value = "";
    },
    [addFiles]
  );

  const onRemove = useCallback((id) => {
    setFiles((prev) => prev.filter((x) => x.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const onRemoveDuplicates = useCallback(() => {
    setFiles((prev) => {
      const seen = new Set();
      const deduped = [];
      for (let i = prev.length - 1; i >= 0; i--) {
        const name = prev[i].file.name;
        if (!seen.has(name)) {
          seen.add(name);
          deduped.unshift(prev[i]);
        }
      }
      return deduped;
    });
  }, []);

  const onReset = useCallback(() => {
    setFiles((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setSelectedId(null);
    setNaiPositiveDump(EMPTY_TEXT);
    setNaiCharPositiveDump(EMPTY_TEXT);
    setNaiNegativeDump(EMPTY_TEXT);
    setNaiCharNegativeDump(EMPTY_TEXT);
    setNaiModelDump(EMPTY_TEXT);
    setNaiStealthDump(EMPTY_TEXT);
  }, []);

  const selected = useMemo(
    () => files.find((f) => f.id === selectedId) || null,
    [files, selectedId]
  );

  // 安全 JSON 解析器
  const tryParseJSON = useCallback((text) => {
    if (!text || typeof text !== "string") return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }, []);

  // 從 Stealth 原始資料抽取值
  const extractFromStealth = useCallback(
    (out) => {
      const raw = out?.stealth?.raw || "";
      const rootObj = tryParseJSON(raw);
      const commentStr =
        (rootObj && typeof rootObj.Comment === "string" && rootObj.Comment) || "";
      const commentObj = tryParseJSON(commentStr) || {};

      // 基本/角色提示詞
      const pos = (commentObj.prompt || "").trim();
      const charPos = (commentObj?.v4_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // 負向提示詞
      const neg =
        (commentObj?.v4_negative_prompt?.caption?.base_caption || "").trim() ||
        (commentObj.uc || "").trim();

      const charNeg = (commentObj?.v4_negative_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // 模型/設定
      const steps = commentObj?.steps;
      const sampler = commentObj?.sampler;
      const noise = commentObj?.noise_schedule;
      const seed = commentObj?.seed;
      const width = commentObj?.width;
      const height = commentObj?.height;
      const scale = commentObj?.scale;
      const cfgRescale = commentObj?.cfg_rescale;

      // Source 優先順序
      const source =
        rootObj?.Source ||
        out?.pngTexts?.texts?.Source ||
        out?.webpMeta?.customTexts?.Source ||
        "";

      const modelLines = [];
      if (steps != null) modelLines.push(`Steps: ${steps}`);
      if (sampler != null) modelLines.push(`Sampler: ${sampler}`);
      if (noise != null) modelLines.push(`Noise Schedule: ${noise}`);
      if (seed != null) modelLines.push(`Seed: ${seed}`);
      if (width != null && height != null) modelLines.push(`Size: ${width} x ${height}`);
      if (scale != null) modelLines.push(`Scale: ${scale}`);
      if (cfgRescale != null) modelLines.push(`CFG Rescale: ${cfgRescale}`);
      if (source) modelLines.push(`Source: ${source}`);

      const prettyStealth = rootObj ? JSON.stringify(rootObj, null, 2) : raw || "";

      return {
        pos,
        charPos,
        neg,
        charNeg,
        modelDump: modelLines.join("\n"),
        stealthPretty: prettyStealth,
      };
    },
    [tryParseJSON]
  );

  // 拖放
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDragOver = (e) => {
      e.preventDefault();
      el.classList.add("dragover");
    };
    const onDragLeave = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
    };
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [addFiles]);

  // novelaiMeta.js 連動
  useEffect(() => {
    let cancelled = false;
    async function run() {
      // 初始化
      setNaiPositiveDump(EMPTY_TEXT);
      setNaiCharPositiveDump(EMPTY_TEXT);
      setNaiNegativeDump(EMPTY_TEXT);
      setNaiCharNegativeDump(EMPTY_TEXT);
      setNaiModelDump(EMPTY_TEXT);
      setNaiStealthDump(EMPTY_TEXT);

      if (!selected?.file) return;

      try {
        const file = selected.file;
        const ab = await file.arrayBuffer();
        const out = await parseNovelAIMetadata({
          arrayBuffer: ab,
          mime: file.type,
          imageSource: file, // 用於 stealth_pngcomp 抽取
        });

        // 一次：直接從 Stealth 原始資料抽取
        const fromStealth = extractFromStealth(out);

        // 二次：後備 (使用既有解析結果)
        const fallbackPos = (out?.result?.sampler?.prompts || []).join("\n").trim();
        const fallbackNeg = (out?.result?.sampler?.negative_prompts || [])
          .join("\n")
          .trim();

        const pos = fromStealth.pos || fallbackPos;
        const neg = fromStealth.neg || fallbackNeg;

        if (!cancelled) {
          setNaiPositiveDump(pos || EMPTY_TEXT);
          setNaiCharPositiveDump(fromStealth.charPos || EMPTY_TEXT);
          setNaiNegativeDump(neg || EMPTY_TEXT);
          setNaiCharNegativeDump(fromStealth.charNeg || EMPTY_TEXT);
          setNaiModelDump(fromStealth.modelDump || EMPTY_TEXT);
          setNaiStealthDump(fromStealth.stealthPretty || EMPTY_TEXT);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = `解析錯誤: ${e?.message || String(e)}`;
          setNaiPositiveDump(msg);
          setNaiNegativeDump(EMPTY_TEXT);
          setNaiCharPositiveDump(EMPTY_TEXT);
          setNaiCharNegativeDump(EMPTY_TEXT);
          setNaiModelDump(EMPTY_TEXT);
          setNaiStealthDump(EMPTY_TEXT);
        }
      }
    }
    run();
    return () => {
      let _ = cancelled; // silence eslint
      cancelled = true;
    };
  }, [selected, extractFromStealth]);

  // 通用複製工具
  const copyText = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch {}
  }, []);

  const isEmpty = (v) => !v || v.trim() === "" || v === EMPTY_TEXT;

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "zh-TW" }}>
        <title>AI 圖片 EXIF 擷取工具 | TooLoves</title>
        <meta
          name="description"
          content="可免費擷取 PNG、WebP 圖片中的 EXIF 與文字中繼資料。支援 NovelAI Stealth PNG、提示詞、種子、模型等。"
        />
        <meta
          name="keywords"
          content="PNG, WebP, 免費, EXIF 擷取, 中繼資料, 圖片中繼資料, NovelAI, Stealth PNG, 提示詞擷取, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 初始 theme-color，於執行時透過 useEffect 同步 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="zh_TW" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="AI 圖片 EXIF 擷取工具 | TooLoves" />
        <meta
          property="og:description"
          content="免費擷取 PNG、WebP 圖片的 EXIF 與文字中繼資料。"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-exif.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI 圖片 EXIF 擷取工具 | TooLoves" />
        <meta
          name="twitter:description"
          content="免費擷取 PNG、WebP 圖片的 EXIF 與文字中繼資料。"
        />
        <meta name="twitter:image" content="/og-exif.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/tw" className="brand-link" aria-label="前往 damoa 繁體首頁">
            TooLoves
          </Link>
          <button
            className="mode-toggle"
            onClick={() => setDarkMode((v) => !v)}
            aria-label="切換主題"
          >
            {darkMode ? "亮色模式" : "深色模式"}
          </button>
        </div>
      </header>

      <main className="container exif-container">
        {isMobile ? (
          // 僅行動裝置顯示的說明區塊
          <section className="generator-card" role="region" aria-label="行動裝置支援說明">
            <div className="metadata-section">
              <h3 className="section-title">行動裝置支援說明</h3>
              <p className="hint">此功能不支援行動裝置。</p>
              <div className="terms-actions">
                <Link to="/tw" className="btn" aria-label="返回主頁">
                  返回主頁
                </Link>
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* 左側面板 */}
            <section className="generator-card panel-left">
              <div className="upload-header">
                <h4 className="section-title">上傳圖片</h4>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={onFileChange}
                />
                <button
                  type="button"
                  onClick={onPickClick}
                  aria-label="選擇圖片檔案"
                  className="btn btn-primary"
                >
                  選擇檔案
                </button>
              </div>

              <div ref={dropRef} id="drop-zone">
                將圖片拖放到此處，或使用「選擇檔案」按鈕
              </div>

              <div className="preview-header">
                <h2 className="section-title small">圖片預覽</h2>
                <div className="preview-size-control">
                  <input
                    type="range"
                    min={100}
                    max={1200}
                    value={previewHeight}
                    onChange={(e) => setPreviewHeight(parseInt(e.target.value, 10))}
                  />
                  <span id="preview-size-value">{previewHeight}px</span>
                </div>
              </div>

              <div className="qr-wrap">
                {selected?.url ? (
                  <img
                    className="preview-img"
                    src={selected.url}
                    alt="圖片預覽"
                    height={previewHeight}
                  />
                ) : (
                  <p className="placeholder">尚未選擇圖片</p>
                )}
              </div>

              <div className="actions">
                <button onClick={onRemoveDuplicates}>移除重複</button>
                <button onClick={onReset}>重設</button>
              </div>

              <h2 className="section-title small list-title">圖片清單</h2>
              <ul id="image-list">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className={`file-row ${f.id === selectedId ? "selected" : ""}`}
                    onClick={() => setSelectedId(f.id)}
                  >
                    <span className="file-name">{f.file.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(f.id);
                      }}
                      className="btn btn-secondary"
                    >
                      刪除
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {/* 右側面板 */}
            <section className="generator-card panel-right">
              {/* 分頁 */}
              <ul className="tabs">
                {["NovelAI"].map((label) => {
                  const key = label;
                  const active = activeTab === key;
                  return (
                    <li
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={active ? "active" : ""}
                    >
                      {label}
                    </li>
                  );
                })}
              </ul>

              {/* NovelAI 分頁內容 */}
              <div className={`tab-content ${activeTab === "NovelAI" ? "active" : ""}`}>
                <div className="metadata-section">
                  {/* 正向提示詞 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-positive" className="label-strong">
                        正向提示詞
                      </label>
                      <button
                        onClick={() => copyText(naiPositiveDump)}
                        aria-label="複製正向提示詞"
                        disabled={isEmpty(naiPositiveDump)}
                        className="btn btn-primary"
                      >
                        複製
                      </button>
                    </div>
                    <div
                      id="nai-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiPositiveDump}
                    </div>
                  </div>

                  {/* 角色正向提示詞 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-positive" className="label-strong">
                        角色正向提示詞
                      </label>
                      <button
                        onClick={() => copyText(naiCharPositiveDump)}
                        aria-label="複製角色正向提示詞"
                        disabled={isEmpty(naiCharPositiveDump)}
                        className="btn btn-primary"
                      >
                        複製
                      </button>
                    </div>
                    <div
                      id="nai-char-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharPositiveDump}
                    </div>
                  </div>

                  {/* 負向提示詞 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-negative" className="label-strong">
                        負向提示詞
                      </label>
                      <button
                        onClick={() => copyText(naiNegativeDump)}
                        aria-label="複製負向提示詞"
                        disabled={isEmpty(naiNegativeDump)}
                        className="btn btn-primary"
                      >
                        複製
                      </button>
                    </div>
                    <div
                      id="nai-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiNegativeDump}
                    </div>
                  </div>

                  {/* 角色負向提示詞 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-negative" className="label-strong">
                        角色負向提示詞
                      </label>
                      <button
                        onClick={() => copyText(naiCharNegativeDump)}
                        aria-label="複製角色負向提示詞"
                        disabled={isEmpty(naiCharNegativeDump)}
                        className="btn btn-primary"
                      >
                        複製
                      </button>
                    </div>
                    <div
                      id="nai-char-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharNegativeDump}
                    </div>
                  </div>

                  {/* NAI 模型與設定 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-model" className="label-strong">
                        NAI 模型與設定
                      </label>
                      <button
                        onClick={() => copyText(naiModelDump)}
                        aria-label="複製 NAI 模型與設定"
                        disabled={isEmpty(naiModelDump)}
                        className="btn btn-primary"
                      >
                        複製
                      </button>
                    </div>
                    <div
                      id="nai-model"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiModelDump}
                    </div>
                  </div>

                  {/* Stealth PNG 中繼資料 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-stealth" className="label-strong">
                        Stealth PNG 中繼資料
                      </label>
                      <button
                        onClick={() => copyText(naiStealthDump)}
                        aria-label="複製 Stealth PNG 中繼資料"
                        disabled={isEmpty(naiStealthDump)}
                        className="btn btn-primary"
                      >
                        複製
                      </button>
                    </div>
                    <div
                      id="nai-stealth"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiStealthDump}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法律資訊連結">
          <Link to="/terms" className="footer-link">
            服務條款
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/privacy" className="footer-link">
            隱私權政策
          </Link>
        </nav>
      </footer>
    </div>
  );
}

export default EXIFPage;